import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Search, ExternalLink, X, Loader2 } from "lucide-react";

/**
 * Buscador con autocomplete contra la API LexDuo.
 * Si LexDuo está deshabilitado en este entorno, muestra un aviso.
 *
 * Props:
 *  - kind: "expediente" | "contacto"
 *  - value: int | null (id en LexDuo)
 *  - label, placeholder
 *  - onChange(id | null, data | null)
 *  - disabled
 */
export default function LexduoPicker({ kind = "expediente", value, onChange, label, placeholder, disabled }) {
  const [enabled, setEnabled] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const endpoint = kind === "expediente" ? "/lexduo/expedientes" : "/lexduo/contactos";

  useEffect(() => {
    api.get("/lexduo/status").then(({ data }) => setEnabled(data.enabled)).catch(() => setEnabled(false));
  }, []);

  // Si hay value inicial, resolverlo
  useEffect(() => {
    if (!value || selected?.id === value) return;
    api.get(`${endpoint}/${value}`)
      .then(({ data }) => setSelected(data))
      .catch(() => setSelected({ id: value, _error: true }));
    // eslint-disable-next-line
  }, [value, endpoint]);

  // Search debounced
  useEffect(() => {
    if (!enabled || !query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(endpoint, { params: { search: query, limit: 15 } });
        // Backend ya desenvuelve el envelope, pero defensive por si cambia:
        const list = Array.isArray(data) ? data : (data?.data || data?.items || data?.results || []);
        setResults(list);
      } catch (e) {
        setResults([]);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, enabled, endpoint]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (item) => {
    setSelected(item);
    setOpen(false);
    setQuery("");
    onChange?.(item.id, item);
  };

  const clear = () => {
    setSelected(null);
    onChange?.(null, null);
  };

  const labelText = label || (kind === "expediente" ? "Expediente LexDuo" : "Contacto LexDuo");

  if (enabled === false) {
    return (
      <div>
        <Label className="text-xs uppercase tracking-wider">{labelText}</Label>
        <div className="mt-1 border border-dashed border-[#E5E7EB] bg-[#FBFBF9] rounded-sm p-2 text-[11px] text-neutral-500 flex items-center gap-2">
          <ExternalLink className="w-3 h-3" />
          <span>LexDuo no está conectado en este entorno.</span>
        </div>
      </div>
    );
  }

  const displayFor = (item) => {
    if (!item) return "";
    if (kind === "expediente") {
      // Campos reales de LexDuo: numero_expediente, titulo, cliente_nombre
      const num = item.numero_expediente || item.numero || item.num_expediente || item.referencia || `#${item.id}`;
      const titulo = item.titulo || item.asunto || item.descripcion || "";
      const cliente = item.cliente_nombre || item.cliente || "";
      const parts = [num];
      if (titulo) parts.push(titulo.length > 80 ? titulo.slice(0, 80) + "…" : titulo);
      if (cliente) parts.push(`· ${cliente}`);
      return parts.join(" · ");
    }
    return item.nombre_completo || item.nombre || item.razon_social || `#${item.id}`;
  };

  return (
    <div ref={boxRef} className="relative">
      <Label className="text-xs uppercase tracking-wider">{labelText}</Label>
      {selected && !selected._error ? (
        <div className="mt-1 border border-[#E5E7EB] bg-white rounded-sm p-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-[#0F172A] truncate" data-testid={`${kind}-lexduo-selected`}>
              {displayFor(selected)}
            </div>
            <Badge variant="outline" className="rounded-sm text-[10px] mt-1 border-[#7B1826]/30 text-[#7B1826]">
              LexDuo #{selected.id}
            </Badge>
          </div>
          {!disabled && (
            <button onClick={clear} className="text-neutral-400 hover:text-[#DC2626] p-0.5" data-testid={`${kind}-lexduo-clear`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative mt-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <Input
            disabled={disabled || enabled === null}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder || (kind === "expediente" ? "Buscar expediente por nº o asunto..." : "Buscar contacto...")}
            className="rounded-sm pl-7 h-9 text-sm"
            data-testid={`${kind}-lexduo-search`}
          />
          {open && query.length >= 2 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-sm shadow-md max-h-64 overflow-y-auto">
              {searching && (
                <div className="p-2 text-xs text-neutral-500 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Buscando…
                </div>
              )}
              {!searching && results.length === 0 && (
                <div className="p-3 text-xs text-neutral-500">Sin resultados</div>
              )}
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => pick(r)}
                  className="w-full text-left px-3 py-2 hover:bg-[#FBFBF9] border-b border-[#F3F4F6] last:border-0"
                  data-testid={`${kind}-lexduo-option-${r.id}`}
                >
                  <div className="text-sm text-[#0F172A] truncate">{displayFor(r)}</div>
                  <div className="text-[10px] font-mono text-neutral-400">#{r.id}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
